import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import styles from './styles.module.scss';

const isLocalEnvironment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
   window.location.hostname === '127.0.0.1');

// Password validation requirements
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_REQUIRES_UPPERCASE = true;
const PASSWORD_REQUIRES_NUMBER = true;
const PASSWORD_REQUIRES_SPECIAL = true;

const StaffSignupView = props => {
    const recaptchaRef = useRef();
    const [formErrors, setFormErrors] = useState({
        firstName: false,
        lastName: false,
        email: false,
        password: false,
        passwordStrength: null,
        staffInvite: false
    });
    const [showErrors, setShowErrors] = useState(false);
    const [inviteCodePassed, setInviteCodePassed] = useState(false);
    
    // Store staff invite code and check if it was passed in URL
    useEffect(() => {
        if (props.staffInvite) {
            localStorage.setItem('pendingStaffInviteCode', props.staffInvite);
            setInviteCodePassed(true);
        }
    }, [props.staffInvite]);

    // Password validation function
    const validatePassword = (password) => {
        if (!password) return { valid: false, message: 'Password is required' };
        
        const checks = [];
        
        // Check minimum length
        if (password.length < PASSWORD_MIN_LENGTH) {
            checks.push(`minimum ${PASSWORD_MIN_LENGTH} characters`);
        }
        
        // Check for uppercase letters
        if (PASSWORD_REQUIRES_UPPERCASE && !/[A-Z]/.test(password)) {
            checks.push('at least one uppercase letter');
        }
        
        // Check for numbers
        if (PASSWORD_REQUIRES_NUMBER && !/[0-9]/.test(password)) {
            checks.push('at least one number');
        }
        
        // Check for special characters
        if (PASSWORD_REQUIRES_SPECIAL && !/[!@#$%^&*()_+-=[\]{};':"|,.<>/?]/.test(password)) {
            checks.push('at least one special character');
        }
        
        if (checks.length === 0) {
            return { valid: true, message: null };
        }
        
        return { 
            valid: false, 
            message: `Password must contain ${checks.join(', ')}`
        };
    };

    // Updated form validation that includes password strength
    const validateForm = () => {
        const passwordValidation = validatePassword(props.password);
        
        const errors = {
            firstName: !props.firstName,
            lastName: !props.lastName,
            email: !props.email,
            password: !props.password,
            passwordStrength: props.password ? (passwordValidation.valid ? null : passwordValidation.message) : null,
            staffInvite: inviteCodePassed ? false : !props.staffInvite
        };
        
        setFormErrors(errors);
        setShowErrors(true);
        
        // Form is valid if no fields are empty and password meets strength requirements
        return !Object.values(errors).some(val => val !== null && val !== false) && passwordValidation.valid;
    };

    // Handle password change with live validation feedback
    const handlePasswordChange = (event) => {
        props.updatePassword(event.target.value);
        
        if (showErrors) {
            const passwordValidation = validatePassword(event.target.value);
            setFormErrors(prev => ({
                ...prev,
                password: !event.target.value,
                passwordStrength: event.target.value ? (passwordValidation.valid ? null : passwordValidation.message) : null
            }));
        }
    };

    const onSubmitWithReCAPTCHA = async () => {
        // Validate the form
        if (!validateForm()) {
            return;
        }
        
        // Initialise token only for local builds
        let token = isLocalEnvironment ? 'bypass_token_for_development' : null;

        // Only execute reCAPTCHA if not in local environment
        if (!isLocalEnvironment && recaptchaRef.current) {
            try {
                token = await recaptchaRef.current.executeAsync();
            } catch (error) {
                console.error('ReCAPTCHA execution failed:', error);
                alert('Could not verify you are human. Please try again.');
                return;           // abort submission in production
            }
        }

        // Create organization name from user's name
        const organizationName = `${props.firstName}'s Organization`;

        // Register the user with organization for staff signup
        props.register(
            token, 
            props.email, 
            props.password, 
            organizationName, // Create personal organization for staff
            false, // not beta
            null, // no athlete invite for staff signup
            props.firstName,
            props.middleName,
            props.lastName
        );

        // Reset the recaptcha if needed
        if (!isLocalEnvironment && recaptchaRef.current) {
            try {
                recaptchaRef.current.reset();
            } catch (error) {
                console.error('Failed to reset reCAPTCHA:', error);
            }
        }
    }

    if (props.registered) {
        // Show different messages based on account activation status
        if (props.is_active) {
            return (<div className={styles.success}>
                <h2>Your staff account has been created successfully!</h2>
                <p>You can now access all features of the RepOne platform.</p>
                {props.staffInvite && (
                    <div className={styles.buttonContainer}>
                        <Link 
                            to={`/staff-invite/${props.staffInvite}`}
                            className={styles.button}
                        >
                            Go back to the invitation
                        </Link>
                    </div>
                )}
            </div>);
        } else {
            return (<div className={styles.error}>
                <h2>We're setting up your RepOne staff account, you'll receive an email when it has been activated</h2>
                <p>Tip: Bookmark this address so you can return to the RepOne portal with a single click</p>
            </div>);
        }
    }

    // If we get here, the user is not registered yet
    let submitText = props.registering ? 'Creating...' : 'Create Staff Account';
    
    return (
        <div>
            <div className={styles.main}>
                <img src="https://assets.reponestrength.com/black_logo.png" alt="RepOne Logo" className={styles.logo} />
                <div>Create your RepOne Staff Account</div>

                <form className={styles.form} autoComplete={"on"} onSubmit={e => { e.preventDefault(); onSubmitWithReCAPTCHA(); }}>
                    <label>First Name*</label>
                    <input type="text"
                        className={`${styles.textInput} ${showErrors && formErrors.firstName ? styles.inputError : ''}`}
                        value={props.firstName}
                        onChange={(event) => props.updateFirstName(event.target.value)} 
                        required
                    />
                    {showErrors && formErrors.firstName && <div className={styles.errorMessage}>First name is required</div>}
                    
                    <label>Middle Name</label>
                    <input type="text"
                        className={styles.textInput}
                        value={props.middleName}
                        onChange={(event) => props.updateMiddleName(event.target.value)}
                    />
                    
                    <label>Last Name*</label>
                    <input type="text"
                        className={`${styles.textInput} ${showErrors && formErrors.lastName ? styles.inputError : ''}`}
                        value={props.lastName}
                        onChange={(event) => props.updateLastName(event.target.value)}
                        required
                    />
                    {showErrors && formErrors.lastName && <div className={styles.errorMessage}>Last name is required</div>}
                    
                    <label>Email address*</label>
                    <input type="email"
                        className={`${styles.textInput} ${showErrors && formErrors.email ? styles.inputError : ''}`}
                        value={props.email}
                        onChange={(event) => props.updateEmail(event.target.value)}
                        required
                    />
                    {showErrors && formErrors.email && <div className={styles.errorMessage}>Email is required</div>}
                    
                    <label>Password*</label>
                    <input type="password"
                        className={`${styles.textInput} ${showErrors && (formErrors.password || formErrors.passwordStrength) ? styles.inputError : ''}`}
                        value={props.password}
                        onChange={handlePasswordChange}
                        required
                    />
                    {showErrors && formErrors.password && <div className={styles.errorMessage}>Password is required</div>}
                    {showErrors && formErrors.passwordStrength && <div className={styles.errorMessage}>{formErrors.passwordStrength}</div>}
                    
                    {!inviteCodePassed && (
                        <>
                            <label>Staff Invitation Code*</label>
                            <input 
                                type="text"
                                className={`${styles.textInput} ${showErrors && formErrors.staffInvite ? styles.inputError : ''}`}
                                value={props.staffInvite}
                                onChange={(event) => props.updateStaffInviteCode(event.target.value)}
                                required
                            />
                            {showErrors && formErrors.staffInvite && <div className={styles.errorMessage}>Staff invitation code is required</div>}
                        </>
                    )}
                    
                    {/* Only render reCAPTCHA component if not in local environment */}
                    {!isLocalEnvironment && (
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            size="invisible"
                            sitekey="6LdxPboZAAAAADdtY2YARZm2eOlRlkt5XAebeUgT"
                        />
                    )}
                    
                    <input
                        className={styles.submit}
                        type="submit"
                        value={submitText}
                        disabled={props.registering}
                    />
                </form>
            </div>
        </div>
    );
};

export default StaffSignupView;